import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import ReactECharts from 'echarts-for-react';

const DashboardView = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [graphsData, setGraphsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [dashResponse, graphsResponse] = await Promise.all([
        axios.get(`${API}/sheets/Dashboard`),
        axios.get(`${API}/sheets/Graphs`)
      ]);
      
      setDashboardData(dashResponse.data.data);
      setGraphsData(graphsResponse.data.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const parseKPIs = () => {
    if (!dashboardData || dashboardData.length < 10) return {};
    
    return {
      revenue: dashboardData[4]?.[1] || '0',
      grossMargin: dashboardData[5]?.[1] || '0',
      ebitda: dashboardData[6]?.[1] || '0',
      pat: dashboardData[7]?.[1] || '0',
      closingCash: dashboardData[8]?.[1] || '0'
    };
  };

  const parseProductData = () => {
    if (!graphsData || graphsData.length < 5) return { categories: [], revenue: [], cogs: [], grossProfit: [] };
    
    const categories = [];
    const revenue = [];
    const cogs = [];
    const grossProfit = [];
    
    let startRow = -1;
    for (let i = 0; i < graphsData.length; i++) {
      if (graphsData[i][0] === 'Product' && graphsData[i][1] === 'Revenue') {
        startRow = i + 1;
        break;
      }
    }
    
    if (startRow > 0) {
      for (let i = startRow; i < startRow + 3 && i < graphsData.length; i++) {
        const row = graphsData[i];
        if (row[0]) {
          categories.push(row[0]);
          revenue.push(parseFloat(row[1]?.toString().replace(/,/g, '')) || 0);
          cogs.push(parseFloat(row[2]?.toString().replace(/,/g, '')) || 0);
          grossProfit.push(parseFloat(row[3]?.toString().replace(/,/g, '')) || 0);
        }
      }
    }
    
    return { categories, revenue, cogs, grossProfit };
  };

  const parseBridgeData = () => {
    if (!graphsData || graphsData.length < 10) return { categories: [], values: [] };
    
    const categories = [];
    const values = [];
    
    let startRow = -1;
    for (let i = 0; i < graphsData.length; i++) {
      if (graphsData[i][0] === 'Bridge Item' && graphsData[i][1] === 'Amount') {
        startRow = i + 1;
        break;
      }
    }
    
    if (startRow > 0) {
      for (let i = startRow; i < startRow + 7 && i < graphsData.length; i++) {
        const row = graphsData[i];
        if (row[0]) {
          categories.push(row[0]);
          values.push(parseFloat(row[1]?.toString().replace(/,/g, '')) || 0);
        }
      }
    }
    
    return { categories, values };
  };

  const parseCashFlowData = () => {
    if (!graphsData || graphsData.length < 3) return { months: [], values: [] };
    
    let monthsRow = null;
    let closingCashRow = null;
    
    for (let i = 0; i < graphsData.length; i++) {
      const row = graphsData[i];
      if (row[1] && row[1].toString().includes('Apr')) {
        monthsRow = row;
      }
      if (row[0] === 'Closing Cash') {
        closingCashRow = row;
      }
    }
    
    if (!closingCashRow) return { months: [], values: [] };
    
    const months = monthsRow ? monthsRow.slice(1, 13) : 
                   ['Apr-25', 'May-25', 'Jun-25', 'Jul-25', 'Aug-25', 'Sep-25',
                    'Oct-25', 'Nov-25', 'Dec-25', 'Jan-26', 'Feb-26', 'Mar-26'];
    
    const values = [];
    for (let i = 0; i < 12; i++) {
      values.push(parseFloat(closingCashRow[i + 1]?.toString().replace(/,/g, '')) || 0);
    }
    
    return { months, values };
  };

  const getProductChartOption = () => {
    const data = parseProductData();
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        data: ['Revenue', 'COGS', 'Gross Profit'],
        bottom: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.categories
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Revenue',
          type: 'bar',
          data: data.revenue,
          itemStyle: { color: '#1e40af' }
        },
        {
          name: 'COGS',
          type: 'bar',
          data: data.cogs,
          itemStyle: { color: '#3b82f6' }
        },
        {
          name: 'Gross Profit',
          type: 'bar',
          data: data.grossProfit,
          itemStyle: { color: '#60a5fa' }
        }
      ]
    };
  };

  const getBridgeChartOption = () => {
    const data = parseBridgeData();
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.categories,
        axisLabel: {
          rotate: 30,
          fontSize: 11
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Amount',
          type: 'bar',
          data: data.values,
          itemStyle: { color: '#2563eb' }
        }
      ]
    };
  };

  const getCashFlowChartOption = () => {
    const data = parseCashFlowData();
    return {
      tooltip: {
        trigger: 'axis'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.months,
        boundaryGap: false
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Closing Cash',
          type: 'line',
          data: data.values,
          smooth: true,
          lineStyle: { color: '#2563eb', width: 3 },
          itemStyle: { color: '#2563eb' },
          areaStyle: { color: 'rgba(37, 99, 235, 0.1)' }
        }
      ]
    };
  };

  if (loading) {
    return <div className="p-6 text-slate-600">Loading dashboard...</div>;
  }

  const kpis = parseKPIs();

  return (
    <div className="space-y-6 fade-in">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
          <div className="text-xs text-slate-500 mb-1 font-medium">Revenue (Year)</div>
          <div className="text-xl font-bold text-slate-800">{parseFloat(kpis.revenue).toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
          <div className="text-xs text-slate-500 mb-1 font-medium">Gross Margin %</div>
          <div className="text-xl font-bold text-slate-800">{kpis.grossMargin}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
          <div className="text-xs text-slate-500 mb-1 font-medium">EBITDA %</div>
          <div className="text-xl font-bold text-slate-800">{kpis.ebitda}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
          <div className="text-xs text-slate-500 mb-1 font-medium">PAT (Year)</div>
          <div className="text-xl font-bold text-slate-800">{parseFloat(kpis.pat).toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
          <div className="text-xs text-slate-500 mb-1 font-medium">Closing Cash</div>
          <div className="text-xl font-bold text-slate-800">{parseFloat(kpis.closingCash).toLocaleString()}</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-700 mb-4">Product-wise Revenue/COGS/GP</h3>
          <ReactECharts option={getProductChartOption()} style={{ height: '320px' }} />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-700 mb-4">EBITDA Bridge (Totals)</h3>
          <ReactECharts option={getBridgeChartOption()} style={{ height: '320px' }} />
        </div>
      </div>

      {/* Cash Flow Chart */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-700 mb-4">Monthly Closing Cash</h3>
        <ReactECharts option={getCashFlowChartOption()} style={{ height: '320px' }} />
      </div>
    </div>
  );
};

export default DashboardView;
